import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2, Briefcase, Clock, Users, Shield, ArrowRight,
  MoreVertical, Trash2, BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrgMembership } from "@/contexts/ActiveOrgContext";

export const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  superadmin: "Super Admin",
  comptable: "Comptable",
  auditeur: "Auditeur",
  lecteur: "Lecteur",
};

export const ROLE_COLORS: Record<string, string> = {
  admin: "bg-primary/10 text-primary",
  superadmin: "bg-accent/20 text-accent-foreground",
  comptable: "bg-blue-500/10 text-blue-600",
  auditeur: "bg-muted text-muted-foreground",
  lecteur: "bg-muted text-muted-foreground",
};

interface OrgCardProps {
  membership: OrgMembership;
  onEnter: () => void;
  onDelete?: () => void;
  dossierCount?: number;
  lastActivity?: string | null;
}

export function OrgCard({ membership, onEnter, onDelete, dossierCount = 0, lastActivity }: OrgCardProps) {
  const org = membership.organization;
  const isCabinet = org.type === "cabinet";
  const canDelete = membership.role === "admin" || membership.role === "superadmin";

  return (
    <Card className="group hover:shadow-lg hover:border-primary/30 transition-all duration-200 cursor-pointer relative">
      <CardContent className="p-5 flex flex-col h-full" onClick={onEnter}>
        <div className="flex items-start justify-between mb-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
            isCabinet ? "bg-primary/10" : "bg-accent/15"
          )}>
            {isCabinet ? <Briefcase className="w-6 h-6 text-primary" /> : <Building2 className="w-6 h-6 text-accent-foreground" />}
          </div>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Badge variant="outline" className="capitalize text-[10px]">{org.type}</Badge>
            {canDelete && onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onDelete} className="gap-2 text-destructive focus:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <h3 className="text-lg font-bold mb-1 group-hover:text-primary transition-colors">{org.name}</h3>
        <div className="flex items-center gap-2 mb-3">
          <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium", ROLE_COLORS[membership.role])}>
            {membership.role === "superadmin" && <Shield className="w-3 h-3" />}
            {ROLE_LABELS[membership.role] ?? membership.role}
          </span>
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {org.user_count} membre(s)</span>
          <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {dossierCount} dossier(s)</span>
          {lastActivity && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(lastActivity).toLocaleDateString("fr-FR")}
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center justify-end text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs font-medium flex items-center gap-1">
            Entrer <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
